import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/shared/config/env.js';

export const WHATSAPP_INBOX_EVENTS = {
  join: 'whatsapp-inbox.join',
  message: 'whatsapp-inbox.message',
  error: 'whatsapp-inbox.error',
} as const;

export interface WhatsAppInboxSocketMessagePayload {
  readonly placeId: string;
  readonly message: WhatsAppInboxMessage;
}

/** Gateway emits the message itself. A `{ placeId, message }` wrapper is also accepted. */
export function readWhatsAppInboxRealtimeMessage(payload: unknown): WhatsAppInboxMessage | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  if (isInboxMessage(record)) {
    return record as unknown as WhatsAppInboxMessage;
  }
  const nested = record['message'];
  if (
    typeof nested === 'object' &&
    nested !== null &&
    isInboxMessage(nested as Record<string, unknown>)
  ) {
    return nested as WhatsAppInboxMessage;
  }
  return null;
}

export function readWhatsAppInboxRealtimePlaceId(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const placeId = (payload as Record<string, unknown>)['placeId'];
  return typeof placeId === 'string' ? placeId : null;
}

function isInboxMessage(record: Record<string, unknown>): boolean {
  return typeof record['id'] === 'string' && typeof record['from'] === 'string';
}

export interface WhatsAppInboxSocketErrorPayload {
  readonly code?: string;
  readonly message?: string;
}

/** Own factory — cross-feature imports from conversation are forbidden. */
export function createWhatsAppInboxSocket(accessToken: string): Socket {
  const baseUrl = env.VITE_API_BASE_URL.length > 0 ? env.VITE_API_BASE_URL : window.location.origin;
  return io(baseUrl, {
    transports: ['websocket'],
    auth: { token: accessToken },
    reconnection: true,
    reconnectionDelay: 500,
  });
}
